import axios from "axios";
import Uri from "jsuri";
import { decodeToken } from "helpers/token";
import { omit, isBoolean, pick, isEmpty } from "lodash";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import { SET_CURRENT_USER } from "constants/ActionTypes";
import {
  AUTH_URL,
  RESET_PASSWORD_URL,
  USER_PERMISSION_URL,
  USER_URL,
  COMPANY_PERMISSION_URL,
  COMPANY_URL,
  NON_UPGRADED_COMPANY_URL,
  GLOBAL_ENV
} from "constants/AppConstants";
import { SrmBoolean, PermissionValue, getEnvConstant } from "helpers/helper";
import { FlaskReq } from "helpers/Request";

export function setCurrentUser(user) {
  return {
    type: SET_CURRENT_USER,
    user
  };
}

export function refreshToken(existingToken) {
  return dispatch => {
    return axios
      .post("/rest-api/api-token-refresh/", { token: existingToken })
      .then(res => {
        const newToken = res.data.token;
        localStorage.setItem("jwtToken", newToken);
      });
  };
}

export function logout() {
  return (dispatch, getState) => {
    // logout from server
    const user = getState().auth.user;

    axios.delete(AUTH_URL);
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("isOperator");
    localStorage.removeItem("flaskToken");
    localStorage.removeItem("flask_url");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("defaultTerminal");
    localStorage.removeItem("gatewayToken");
    localStorage.removeItem("verified");

    const event = new Event("logout");
    window.dispatchEvent(event);
    AuthorizationToken.delete();
    dispatch(setCurrentUser({}));

    if (user?.is_tis === true && user?.company_sso_enabled === true) {
      const redirectUrl = window.location.search
        ? window.location.href
        : window.location.origin;
      window.location.href = new Uri(getEnvConstant(GLOBAL_ENV.TIS_LOGOUT_URL))
        .addQueryParam("returnTo", redirectUrl)
        .toString();
    }
  };
}

export const updateCurrentUser = () => async (dispatch, getState) => {
  let userSrmUid;
  let companyId;
  let upgraded = false;
  let companySsoEnabled = false;
  let companyFeatures = {};
  let gatewayFeatures = {};
  let email = "";
  try {
    const auth = getState().auth;
    userSrmUid = auth.user.srm_uid;
    companyId = auth.user.companyId;
    upgraded = auth.user.upgraded;
    companySsoEnabled = auth.user?.company_sso_enabled || false;
    companyFeatures = auth.user?.companyFeatures;
    gatewayFeatures = auth.user?.gatewayFeatures;
    email = auth.user?.email;
  } catch (e) {
    dispatch(logout());
    return;
  }

  const decodedToken = decodeToken();
  companySsoEnabled = companySsoEnabled || decodedToken.company_sso_enabled;
  const companySsoConfig = await getTisConfig(companyId, upgraded, email);
  const systemInfo = await getSystemInfo();

  if (upgraded) {
    const permissionRes = await axios.get(
      `${USER_PERMISSION_URL}${decodedToken.user_id}`
    );

    const userRes = await axios.get(`${USER_URL}${decodedToken.user_id}`);

    const comapnyPermissionRes = await axios.get(
      `${COMPANY_PERMISSION_URL}${companyId}`
    );

    const permissions = generateNewPermissionData(
      comapnyPermissionRes.data,
      permissionRes.data.divisions,
      permissionRes.data.locations
    );
    const { docDivisions, docLocations } = getDocDivision(
      companyId,
      permissions.divisions,
      permissions.locations
    );
    const user_data = {
      ...userRes.data,
      company_sso_enabled: companySsoEnabled,
      // TODO: remove compperms
      compperms: comapnyPermissionRes.data,
      permissions: permissions.permissions,
      locations: docLocations,
      divisions: docDivisions,
      useTexadaPay: comapnyPermissionRes.data.use_texada_pay,
      companyId: companyId,
      upgraded: comapnyPermissionRes.data.upgraded,
      isSuperAdmin: userRes.data.is_super_admin,
      companySsoConfig,
      companyFeatures: {
        ...companyFeatures,
        customer_owned_equipment:
          systemInfo.cust_owned_equipment === SrmBoolean.True
      },
      gatewayFeatures,
      systemInfo
    };
    console.log("new-user-data", user_data);

    localStorage.setItem("currentUser", JSON.stringify(user_data));
    dispatch(setCurrentUser(user_data));
    return;
  }

  return axios
    .get(`/rest-api/company/${companyId}/user/${userSrmUid}`)
    .then(res => {
      const { user, comp_perms, use_texada_pay, upgraded } = res.data;
      const permissions = generateOldPermissionData(
        comp_perms,
        res.data.divisions,
        res.data.locations
      );
      const { docDivisions, docLocations } = getDocDivision(
        companyId,
        permissions.divisions,
        permissions.locations
      );
      const user_data = {
        ...user,
        company_sso_enabled: companySsoEnabled,
        isSuperAdmin: user.is_super_admin,
        // TODO: remove compperms
        compperms: comp_perms,
        permissions: {
          ...permissions.permissions,
          admin: user.settings_menu_access,
          update_settings: user.settings_menu_access,
          capture_signatures: user.capture_signatures,
          change_asset_status: user.change_asset_status,
          maintain_sites: user.maintain_sites,
          update_meter_reading: user.update_meter_reading,
          view_inventory: user.view_inventory,
          workorder_default_reporting: user.workorder_default_reporting
        },
        locations: docLocations,
        divisions: docDivisions,
        useTexadaPay: use_texada_pay,
        companyId,
        upgraded,
        companySsoConfig,
        companyFeatures: {
          ...res.data?.company_features,
          customer_owned_equipment:
            systemInfo.cust_owned_equipment === SrmBoolean.True
        },
        gatewayFeatures: res.data?.gateway_features,
        systemInfo
      };
      console.log("user-data", user_data);

      localStorage.setItem("currentUser", JSON.stringify(user_data));
      dispatch(setCurrentUser(user_data));
    });
};

async function getTisConfig(companyId, upgraded, email) {
  try {
    if (upgraded) {
      const companySsoRes = await axios.post(
        `${COMPANY_URL}/${companyId}/sso`,
        { email }
      );
      return companySsoRes.data;
    }
    const companySsoRes = await axios.get(
      `${NON_UPGRADED_COMPANY_URL}/${companyId}/tis_config`
    );
    return companySsoRes.data;
  } catch (err) {
    console.log(err.response);
    return {};
  }
}

async function getSystemInfo() {
  try {
    const systemInfoRes = await FlaskReq({
      method: "get",
      url: `${localStorage.flask_url}/system_info/`
    });
    return systemInfoRes.data;
  } catch (err) {
    console.log(err.response);
    return {};
  }
}

export function resetPassword(data) {
  return dispatch => {
    return axios
      .put(`${RESET_PASSWORD_URL}/${encodeURIComponent(data.uid)}/`, {
        password: data.password
      })
      .then(res => {
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("isOperator");
        localStorage.removeItem("currentUser");
        AuthorizationToken.delete();
        dispatch(setCurrentUser({}));
      });
  };
}

function getDocDivision(companyId, divisions, locations) {
  let mappedDivisions = [...divisions];
  let mappedLocations = [...locations];
  const docAccess = JSON.parse(localStorage.getItem("docDivisionAccess"));

  if (isEmpty(docAccess?.[companyId])) {
    return { docDivisions: divisions, docLocations: locations };
  }

  const docDivisions = docAccess[companyId].divisions;
  const docLocations = docAccess[companyId].locations;

  mappedDivisions.forEach((division, index) => {
    const target = docDivisions.find(
      e => e.srm_uid === division.warehouse.srm_uid
    );

    if (!isEmpty(target)) {
      mappedDivisions[index] = {
        ...mappedDivisions[index],
        ...target
      };
    }
  });

  mappedLocations.forEach((location, index) => {
    const target = docLocations.find(
      e => e.srm_uid === location.warehouse.srm_uid
    );

    if (!isEmpty(target)) {
      mappedLocations[index] = {
        ...mappedLocations[index],
        ...target
      };
    }
  });

  return { docDivisions: mappedDivisions, docLocations: mappedLocations };
}

function mergePermissions(divisionsPermission) {
  let permissions = {};
  divisionsPermission.forEach(division => {
    Object.keys(division.permissions).forEach(permissionKey => {
      if (permissions[permissionKey] === undefined) {
        permissions[permissionKey] = division.permissions[permissionKey];
        return;
      }
      if (permissionKey.includes("discount")) {
        permissions[permissionKey] = Math.max(
          Number(permissions[permissionKey]),
          Number(division.permissions[permissionKey])
        );
        return;
      }
      if (isBoolean(permissions[permissionKey])) {
        permissions[permissionKey] =
          permissions[permissionKey] || division.permissions[permissionKey];
      }
    });
  });
  return permissions;
}

export function generateNewPermissionData(
  companyPermission,
  divisions,
  locations
) {
  let divisionsPermission = [];
  divisions.forEach(division => {
    let newPermission = {};
    division.permissions.forEach(i => {
      const keyValuePair = [];
      if (
        ["dispatch", "driver", "mechanic", "yard_inspector"].includes(i.code)
      ) {
        keyValuePair.push({
          key: `is_${i.code}`,
          value: i.value === SrmBoolean.True
        });
      } else if ([SrmBoolean.True, SrmBoolean.False].includes(i.value)) {
        keyValuePair.push({ key: i.code, value: i.value === SrmBoolean.True });
      } else if (
        [
          PermissionValue.VIEW,
          PermissionValue.EDIT,
          PermissionValue.CREATE,
          PermissionValue.DELETE
        ].includes(i.value)
      ) {
        keyValuePair.push({
          key: `${PermissionValue.VIEW}_${i.code}`,
          value: [
            PermissionValue.VIEW,
            PermissionValue.EDIT,
            PermissionValue.CREATE,
            PermissionValue.DELETE
          ].includes(i.value)
        });
        keyValuePair.push({
          key: `${PermissionValue.EDIT}_${i.code}`,
          value: [
            PermissionValue.EDIT,
            PermissionValue.CREATE,
            PermissionValue.DELETE
          ].includes(i.value)
        });
        keyValuePair.push({
          key: `${PermissionValue.CREATE}_${i.code}`,
          value: [PermissionValue.CREATE, PermissionValue.DELETE].includes(
            i.value
          )
        });
        keyValuePair.push({
          key: `${PermissionValue.DELETE}_${i.code}`,
          value: [PermissionValue.DELETE].includes(i.value)
        });
      } else {
        keyValuePair.push({ key: i.code, value: i.value });
      }
      keyValuePair.forEach(p => {
        if (newPermission.hasOwnProperty(p.key)) {
          newPermission[p.key] = newPermission[p.key] || p.value;
        } else {
          newPermission[p.key] = p.value;
        }
      });
    });
    newPermission.all_in_one_transaction =
      !isEmpty(newPermission) && companyPermission.all_in_one_transaction;
    divisionsPermission.push({
      ...omit(division, "permissions"),
      permissions: newPermission
    });
  });

  const mergedPermissions = mergePermissions(divisionsPermission);

  const permissions = {
    ...mergedPermissions,
    ...companyPermission,
    contracts: mergedPermissions.contracts && companyPermission.contract,
    counter: mergePermissions.counter && companyPermission.invoice,
    customer: mergedPermissions.customer && companyPermission.customer,
    dashboard: mergedPermissions.dashboard && companyPermission.dashboard,
    inventory: mergedPermissions.inventory && companyPermission.inventory,
    inventory_receiving:
      mergedPermissions.inventory_receiving &&
      companyPermission.inventory_receiving,
    view_invoices: mergedPermissions.counter && companyPermission.invoice,
    operators: mergedPermissions.operators && companyPermission.operation,
    view_quotations_rental:
      mergedPermissions.quotations && companyPermission.quote,
    view_rental_returns:
      mergedPermissions.view_rental_returns && companyPermission.rental_return,
    view_reservations:
      mergedPermissions.counter && companyPermission.reservation,
    returns: mergedPermissions.returns && companyPermission.returns,
    signature: mergedPermissions.signature && companyPermission.signature,
    tickets: mergedPermissions.tickets && companyPermission.ticket,
    time_tracker: mergedPermissions.operators && companyPermission.time_tracker,
    workorder: mergedPermissions.workorder && companyPermission.workorder,
    booking:
      (mergedPermissions.counter || mergedPermissions.quotations) &&
      (companyPermission.quote ||
        companyPermission.reservation ||
        companyPermission.all_in_one_transaction),
    create_tickets_sub:
      mergedPermissions.create_tickets_sub ||
      mergedPermissions.create_pickup ||
      mergedPermissions.create_delivery ||
      mergedPermissions.create_task,
    capture_signatures: mergedPermissions.signature
  };
  return { permissions, divisions: divisionsPermission, locations };
}

export function generateOldPermissionData(
  companyPermission,
  divisions,
  locations
) {
  let permissions = {};
  const extraList = ["DivLocId", "id", "user", "warehouse", "warehouse_type"];
  let divisionsPermission = [];
  divisions.forEach(division => {
    let newPermission = {};
    Object.keys(division).forEach(permissionKey => {
      if (extraList.includes(permissionKey)) {
        return;
      }
      if (permissionMap[permissionKey] !== undefined) {
        permissionMap[permissionKey].forEach(newKey => {
          newPermission[newKey] =
            newPermission[newKey] || division[permissionKey];
        });
      } else {
        newPermission[permissionKey] =
          newPermission[permissionKey] || division[permissionKey];
      }
    });
    if (companyPermission.returns) {
      newPermission.create_in_yard_returns = true;
    }
    divisionsPermission.push({
      ...pick(division, ...extraList),
      permissions: newPermission
    });
  });

  let locationsPermission = [];
  locations.forEach(location => {
    let newPermission = {};
    Object.keys(location).forEach(permissionKey => {
      if (extraList.includes(permissionKey)) {
        return;
      }
      if (permissionMap[permissionKey] !== undefined) {
        permissionMap[permissionKey].forEach(newKey => {
          newPermission[newKey] =
            newPermission[newKey] || location[permissionKey];
        });
      } else {
        newPermission[permissionKey] =
          newPermission[permissionKey] || location[permissionKey];
      }
    });
    if (companyPermission.returns) {
      newPermission.create_in_yard_returns = true;
    }
    locationsPermission.push({
      ...pick(location, ...extraList),
      permissions: newPermission
    });
  });

  const mergedDivisionsPermission = mergePermissions(divisionsPermission);
  const mergedLocationsPermission = mergePermissions(locationsPermission);

  Object.keys(mergedDivisionsPermission).forEach(permissionKey => {
    if (mergedLocationsPermission[permissionKey] !== undefined) {
      permissions[permissionKey] =
        mergedDivisionsPermission[permissionKey] &&
        mergedLocationsPermission[permissionKey];
      return;
    }
    permissions[permissionKey] = mergedDivisionsPermission[permissionKey];
  });
  Object.keys(mergedLocationsPermission).forEach(permissionKey => {
    if (permissions[permissionKey] === undefined) {
      permissions[permissionKey] = mergedLocationsPermission[permissionKey];
      return;
    }
  });

  permissions = {
    ...permissions,
    ...companyPermission,
    dashboard_sub: companyPermission.dashboard,
    calendar: companyPermission.dashboard,
    all_in_one_transaction:
      permissions.all_in_one_transaction &&
      companyPermission.all_in_one_transaction,
    contracts: permissions.contracts && companyPermission.contract,
    customer: permissions.customer && companyPermission.customer,
    inventory_receiving:
      permissions.inventory_receiving && companyPermission.inventory_receiving,
    view_invoices: permissions.view_invoices && companyPermission.invoice,
    view_reservations:
      permissions.view_reservations && companyPermission.reservation,
    tickets: companyPermission.ticket,
    create_tickets_sub:
      permissions.create_pickup ||
      permissions.create_delivery ||
      companyPermission.ticket,
    view_task: companyPermission.ticket,
    edit_task: companyPermission.ticket,
    create_task: companyPermission.ticket,
    view_ticket_schedule: companyPermission.ticket,
    invoice: permissions.invoice || permissions.invoice_workorder,
    booking:
      permissions.view_quotations_rental ||
      permissions.view_reservations ||
      (permissions.all_in_one_transaction &&
        companyPermission.all_in_one_transaction),
    create_rental_returns: companyPermission.rental_return,
    operators: companyPermission.operation,
    create_time_tracker: companyPermission.time_tracker
  };

  return {
    permissions,
    divisions: divisionsPermission,
    locations: locationsPermission
  };
}

const permissionMap = {
  all_in_one_screen: ["all_in_one_transaction"],
  is_dispatcher: ["is_dispatch"],
  is_driver: ["is_driver"],
  is_mechanic: ["is_mechanic"],
  is_yard_inspector: ["is_yard_inspector"],
  create_contracts: [
    "contracts",
    "create_contracts_sub",
    "edit_contracts_sub",
    "contract_calendar",
    "view_contracts_sub",
    "create_rental_returns"
  ],
  delete_contracts: ["delete_contracts_sub"],
  create_invoices: ["create_invoices", "edit_invoices", "view_invoices"],
  create_rental_quotes: ["create_quotations_rental"],
  create_reservations: ["create_reservations"],
  create_sale_orders: ["create_sales_orders"],
  create_sale_quotes: ["create_quotations_sales"],
  create_workorders: [
    "create_workorder_sub",
    "edit_workorder_sub",
    "view_workorder_sub",
    "create_labour_lines",
    "edit_labour_lines",
    "delete_labour_lines"
  ],
  maintain_customers: [
    "customer",
    "create_customer_sub",
    "create_customer_contact",
    "edit_customer_contact",
    "edit_customer_sub"
  ],
  edit_deliveries: [
    "create_delivery",
    "edit_delivery",
    "view_delivery",
    "view_task",
    "edit_task",
    "create_task",
    "delete_task"
  ],
  edit_pickups: [
    "create_pickup",
    "edit_pickup",
    "view_pickup",
    "view_task",
    "edit_task",
    "create_task",
    "delete_task"
  ],
  view_contracts: ["contracts", "view_contracts_sub"],
  view_customers: ["customer", "view_customer_sub", "view_customer_contact"],
  view_deliveries: [
    "view_delivery",
    "view_task",
    "edit_task",
    "create_task",
    "delete_task"
  ],
  view_invoices: ["view_invoices"],
  view_pickups: [
    "view_pickup",
    "view_task",
    "edit_task",
    "create_task",
    "delete_task"
  ],
  view_rental_quotes: ["view_quotations_rental"],
  view_reservations: ["view_reservations"],
  view_sale_orders: ["view_sales_orders"],
  view_sale_quotes: ["view_quotations_sales"],
  view_workorders: ["workorder", "view_workorder_schedule"],
  add_assets: ["create_assets", "view_assets"],
  add_parts: ["create_parts", "view_parts"],
  edit_assets: ["edit_assets", "view_assets"],
  edit_parts: ["edit_parts", "view_assets"],
  inventory_receiving_access: ["inventory_receiving"],
  view_estimates: ["view_estimates"],
  create_estimates: ["view_estimates", "edit_estimates", "create_estimates"],
  invoice_workorder: ["invoice_workorder"],
  close_workorder: ["close_workorder"],
  cancel_del_pick: ["delete_pickup", "delete_delivery", "delete_task"]
};
